import { Injectable, EventEmitter } from "@angular/core";
import { UserManager, User, WebStorageStateStore } from "oidc-client";
import {
  BehaviorSubject,
  combineLatest,
  Observable,
  ReplaySubject,
  Subject,
} from "rxjs";
import { environment } from '../../../../../environments/environment';
import { map } from "rxjs/operators";
import { LoggingService } from '../../../../framework/logging/logging.service';
import { SeverityLevel } from '../../../../framework/logging/severity-level.model';
import { AuthContext, IAuthContext } from "../models/security/auth-context";
import { AppInjector } from '../../../../app-injector.service';
import { jwtDecode } from "jwt-decode";
import { Router } from "@angular/router";
import { SECURITY_EVENTS } from '../../../../framework/logging/types';
import { JwtHelperService } from '@auth0/angular-jwt';
import { UserActivityService } from '../../../services/user-activity.service';
import { FilterStateService } from '../../../services/filter-state.service';
// import { UserManagementService } from '../../user-management.service';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private _userManager: UserManager;
  private _user: User;
  private _loginChangedSubject = new Subject<boolean>();
  private lastFullAuthTime = Date.now(); // Track when the user last did a full authentication

  // Add token renewal protection
  private _isRenewing = false;
  private _renewalPromise: Promise<User | null> | null = null;
  private _lastRenewalAttempt = 0;
  private _renewalCooldownMs = 30000; // 30 seconds cooldown between renewal attempts

  // Add initialization flag and promise
  private _isInitialized = false;
  private _initializationPromise: Promise<void> | null = null;

  // Token expiry buffer - consider token expired this many seconds before actual expiry
  private readonly TOKEN_EXPIRY_BUFFER_SECONDS = 30;



  //sb auth - reimplement this
  private isAuthenticatedSubject$ = new BehaviorSubject<boolean>(false);
  public isAuthenticated$ = this.isAuthenticatedSubject$.asObservable();

  //sb auth - reimplement this
  private isDoneLoadingSubject$ = new ReplaySubject<boolean>();
  public isDoneLoading$ = this.isDoneLoadingSubject$.asObservable();

  //sb auth - reimplement this
  private isProfileLoadedSubject$ = new BehaviorSubject<boolean>(false);
  public isProfileLoaded$ = this.isProfileLoadedSubject$.asObservable();

  public canActivateProtectedRoutes$: Observable<boolean> = combineLatest([
    this.isAuthenticated$,
    this.isDoneLoading$,
  ]).pipe(map((values) => values.every((b) => b)));

  loginChanged = this._loginChangedSubject.asObservable();

  authContext: AuthContext;


  public authContextUpdated: EventEmitter<IAuthContext> =
    new EventEmitter<IAuthContext>();

  constructor(
    private loggingService: LoggingService,
    protected router: Router,
    private jwtHelper: JwtHelperService,
    private userActivityService: UserActivityService,
    private filterStateService: FilterStateService
    // private userManagementService: UserManagementService
  ) {
    //console.log('🏗️ AuthService constructor called');
    //console.log('📊 Constructor environment check:', {
    //   identityServerIssuer: environment.identityServerIssuer,
    //   identityServerClientId: environment.identityServerClientId,
    //   windowOrigin: window.location.origin,
    //   timestamp: new Date().toISOString()
    // });

    const stsSettings = {
      authority: environment.identityServerIssuer,
      client_id: environment.identityServerClientId,
      redirect_uri: `${window.location.origin}/signin-callback`,
      scope: 'openid profile web_api offline_access',
      response_type: 'code',
      post_logout_redirect_uri: `${window.location.origin}/signout-callback`,
      automaticSilentRenew: false, // Disable automatic renewal - we'll handle it manually
      silent_redirect_uri: `${window.location.origin}/silent-refresh.html`,
      silentRequestTimeout: 30000, // Increase timeout to 30 seconds
      accessTokenExpiringNotificationTime: 120, // Trigger renewal 2 minutes (120 seconds) before expiry
      filterProtocolClaims: true,
      loadUserInfo: false, // Don't load additional user info to keep things simple
      monitorSession: false, // Disable session monitoring to prevent automatic checks
      checkSessionInterval: 0, // Disable periodic session checks
      revokeAccessTokenOnSignout: environment.production ? true : false, // Revoke access tokens on signout in production to reduce token misuse risk; can be disabled in non-prod for mixed-content/local issues
      includeIdTokenInSilentRenew: true, // Ensure we get ID token in renewal
      useRefreshToken: true, // Enable refresh tokens
      extraQueryParams: {
        timestamp: Date.now().toString() // Cache buster
      },
      userStore: new WebStorageStateStore({ store: window.localStorage }) // <-- Persist user in localStorage
    };

    //console.log('⚙️ Creating UserManager with settings:', {
    //   authority: stsSettings.authority,
    //   client_id: stsSettings.client_id,
    //   automaticSilentRenew: stsSettings.automaticSilentRenew,
    //   monitorSession: stsSettings.monitorSession,
    //   userStoreType: 'localStorage'
    // });

    this._userManager = new UserManager(stsSettings);
    console.log('✅ UserManager created successfully');
    console.log('⚙️ UserManager settings:', {
      authority: stsSettings.authority,
      client_id: stsSettings.client_id,
      automaticSilentRenew: stsSettings.automaticSilentRenew,
      useRefreshToken: stsSettings.useRefreshToken
    });

    this._userManager.events.addAccessTokenExpiring((x) => {
      console.log('🔔 Access token expiring event triggered');

      // Log token expiration details
      this._userManager.getUser().then(user => {
        if (user?.expires_at) {
          const expiresInMs = (user.expires_at * 1000) - Date.now();
          const expiresInSeconds = Math.floor(expiresInMs / 1000);
          const expiresInMinutes = Math.floor(expiresInSeconds / 60);
          console.log(`⏰ Token expires in: ${expiresInMinutes}m ${expiresInSeconds % 60}s`);
          console.log(`📅 Token expiry time: ${new Date(user.expires_at * 1000).toLocaleString()}`);
        }
      });

      // Check if we're already renewing to prevent duplicate calls
      if (this._isRenewing) {
        console.log('⏳ Renewal already in progress, skipping expiring event');
        return;
      }

      // Check if user has been idle too long
      if (this.shouldLogoutDueToIdleTimeout()) {
        console.log('🚪 Token expiring but user idle >=15 minutes - redirecting to login');
        this.router.navigate(['/login']);
        this.logout();
        return;
      }

      // Additional check - only proceed if we actually have a user with a refresh token
      this._userManager.getUser().then(user => {
        if (!user?.refresh_token) {
          //console.log('⚠️ No user or refresh token available in expiring event');
          return;
        }

        loggingService.logError('Access token expiring event', SeverityLevel.Information);

        this.renewToken().then((u) => {
          if (u) {
            //console.log('✅ Access token expiring event - renewal success');
            loggingService.logError(
              'Access token expiring event renew success',
              SeverityLevel.Information
            );
          }
        }).catch(error => {
          console.error('❌ Token renewal failed in expiring event:', error);
          // Don't show warnings for cooldown errors or idle timeout errors
          if (!error.message?.includes('cooldown') && !error.message?.includes('inactivity') && !error.message?.includes('expired')) {
            loggingService.logError('Token renewal failed during expiring event', SeverityLevel.Warning);
          }
        });
      }).catch(err => {
        console.error('Error checking user in expiring event:', err);
      });
    });

    this._userManager.events.addAccessTokenExpired((_) => {
      //console.log('🔴 Access token expired event triggered');

      // Check if we're already renewing to prevent duplicate calls
      if (this._isRenewing) {
        //console.log('⏳ Renewal already in progress, skipping expired event');
        return;
      }

      loggingService.logError('Access token expired', SeverityLevel.Information);

      if (this.shouldLogoutDueToIdleTimeout()) {
        console.log('🚪 Token expired after prolonged inactivity - redirecting to login page');
        this.router.navigate(['/login']);
        this.logout();
        return;
      }

      this._userManager.getUser().then(user => {
        if (!user?.refresh_token) {
          console.log('🚪 Token expired with no refresh token available - redirecting to login page');
          this.router.navigate(['/login']);
          this.logout();
          return;
        }

        this.renewToken().then((renewedUser) => {
          if (renewedUser) {
            loggingService.logError('Access token expired event renew success', SeverityLevel.Information);
          }
        }).catch(error => {
          console.error('❌ Token renewal failed in expired event:', error);

          if (error?.message?.includes('inactivity')) {
            this.router.navigate(['/login']);
            this.logout();
            return;
          }

          this.showReauthenticationWarning();
        });
      }).catch(err => {
        console.error('Error checking user in expired event:', err);
        this.showReauthenticationWarning();
      });
      return;

      // Note: The following code is commented out because we no longer attempt renewal for expired tokens
      /*
      // Additional check - only proceed if we actually have a user with a refresh token
      this._userManager.getUser().then(user => {
        if (!user?.refresh_token) {
          //console.log('⚠️ No user or refresh token available in expire event, showing warning');
          this.showReauthenticationWarning();
          return;
        }

        this.renewToken().then((u) => {
          if (u) {
            //console.log('✅ Access token expired event - renewal success');
            loggingService.logError('Access token expired event renew success', SeverityLevel.Information);
          }
        }).catch(error => {
          console.error('❌ Token renewal failed in expired event:', error);

          // Don't show warnings for cooldown errors
          if (!error.message?.includes('cooldown')) {
            loggingService.logError('Token renewal failed during expired event', SeverityLevel.Warning);
            this.showReauthenticationWarning();
          }
        });
      }).catch(err => {
        console.error('Error checking user in expired event:', err);
        this.showReauthenticationWarning();
      });
      */
    });

    this._userManager.events.addUserLoaded((user) => {
      //console.log('🔄 UserLoaded event triggered:', {
      //   hasUser: !!user,
      //   userExpired: user?.expired,
      //   hasAccessToken: !!user?.access_token,
      //   hasRefreshToken: !!user?.refresh_token,
      //   isSameUser: this._user === user
      // });

      if (this._user !== user) {
        this._user = user;

        // Only load security context if we have a valid, non-expired user
        if (user && !user.expired && user.access_token) {
          this.loadSecurityContext();
          this._loginChangedSubject.next(true);
          this.setIsLoadingSubject(true);
          //console.log('✅ User loaded successfully - setting authenticated state');
        } else {
          console.warn('⚠️ User loaded but is expired or missing token');
          // Don't immediately set to false - let other mechanisms handle this
          // this._loginChangedSubject.next(false);
          // this.setIsLoadingSubject(false);
        }
      }
    });

    // Add user unloaded event to track when users are getting logged out
    this._userManager.events.addUserUnloaded(() => {
      //console.log('🚨 UserUnloaded event triggered');
      //console.log('Current user state before unload:', {
      //   hasUser: !!this._user,
      //   expired: this._user?.expired,
      //   isRenewing: this._isRenewing
      // });

      // CRITICAL: Do NOT clear state during renewal or if we have a valid user
      // This prevents logout during token refresh
      if (this._isRenewing) {
        //console.log('⏳ Ignoring UserUnloaded event - renewal in progress');
        return;
      }

      // Double-check if we actually have a valid user before clearing state
      this._userManager.getUser().then(user => {
        if (user && !user.expired && user.access_token) {
          //console.log('✅ User still valid despite UserUnloaded event - keeping session');
          return;
        }

        // Only clear state if user is actually gone or expired
        //console.log('❌ User truly unloaded - clearing authentication state');
        this._user = null;
        this.authContext = null;
        this.setLoginState(false, 'UserUnloaded event');
        this.setIsLoadingSubject(false);
      }).catch(err => {
        console.error('Error checking user in UserUnloaded event:', err);
        // On error, be conservative and don't clear state
        //console.log('⚠️ Error checking user - not clearing state to be safe');
      });
    });

    // Add session changed event to track session issues
    this._userManager.events.addUserSessionChanged(() => {
      //console.log('🔄 User session changed event triggered');
      this.logAuthState('Session Changed');
    });

    // Add silent renew error event to track renewal failures
    this._userManager.events.addSilentRenewError((error) => {
      console.error('🚨 Silent renew error event:', error);
      this.loggingService.logError('Silent renewal error event triggered', SeverityLevel.Warning);

      // Don't automatically logout on silent renew errors - let our renewal logic handle it
      if (!this._isRenewing) {
        this.showReauthenticationWarning();
      }
    });

    // Subscribe to idle timeout from UserActivityService
    this.userActivityService.idleTimeout$.subscribe(() => {
      const idleMinutes = Math.floor(this.userActivityService.getIdleTimeoutMs() / 60000);
      console.log(`⏰ Idle timeout detected - user has been inactive for ${idleMinutes} minutes`);
      this.loggingService.logEvent(SECURITY_EVENTS.SESSION_EXPIRED, {
        reason: 'idle_timeout',
        idleMinutes
      });

      // Use proper OIDC logout flow which will redirect via post_logout_redirect_uri
      this.logout();
    });
  }

  login() {
    // Update the last full auth time when user initiates a login
    this.lastFullAuthTime = Date.now();

    //console.log('usermanager tasks: ', this._userManager);
    localStorage.setItem("isLoggingIn", "true")
    this.lastFullAuthTime = Date.now();  // Update the last auth time
    return this._userManager.signinRedirect();
  }

  isLoggedIn(): Promise<boolean> {
    //console.log('🔍 isLoggedIn() method called');
    //console.log('Call stack:', new Error().stack?.split('\n').slice(1, 4).join('\n'));

    return this.initialize().then(() => {
      //console.log('✅ Auth service initialized, getting user...');
      return this._userManager.getUser();
    }).then((user) => {
      //console.log('🔍 isLoggedIn check - user state:', {
      //   hasUser: !!user,
      //   expired: user?.expired,
      //   hasAccessToken: !!user?.access_token,
      //   hasRefreshToken: !!user?.refresh_token,
      //   expiresAt: user?.expires_at,
      //   timeUntilExpiry: user?.expires_at ? (user.expires_at * 1000) - Date.now() : null
      // });

      const userCurrent = !!user && !user.expired;

      //console.log('🔍 isLoggedIn evaluation:', {
      //   hasUser: !!user,
      //   expired: user?.expired,
      //   userCurrent: userCurrent,
      //   wouldChangeState: this._user !== user,
      //   isRenewing: this._isRenewing,
      //   userObjectComparison: {
      //     currentUserRef: this._user === user ? 'SAME' : 'DIFFERENT',
      //     currentUserAccessToken: this._user?.access_token?.substring(0, 20) || 'None',
      //     newUserAccessToken: user?.access_token?.substring(0, 20) || 'None'
      //   }
      // });

      // Only update state if the user actually changed
      if (this._user !== user) {
        //console.log('👤 User state changed in isLoggedIn, updating...');
        //console.log('User change details:', {
        //   previousUser: this._user ? 'Had user' : 'No user',
        //   newUser: user ? 'Has user' : 'No user',
        //   userExpiredState: user?.expired,
        //   tokenChanged: this._user?.access_token !== user?.access_token
        // });

        this._user = user;

        // CRITICAL: Don't update login state during renewal to prevent logout
        if (!this._isRenewing) {
          //console.log(`🔄 Updating login state to: ${userCurrent} (not during renewal)`);
          this.setLoginState(userCurrent, 'isLoggedIn method');
          this.setIsLoadingSubject(userCurrent);
        } else {
          //console.log('⏳ Skipping state update during renewal to prevent logout');
        }

        // Load security context if user is valid and we don't have context yet
        if (userCurrent && !this.authContext) {
          //console.log('🔐 Loading security context for valid user...');
          this.loadSecurityContext();
        }
      } else {
        //console.log('👤 User state unchanged in isLoggedIn - no updates needed');
      }

      //console.log(`🔍 isLoggedIn() returning: ${userCurrent}`);
      return userCurrent;
    }).catch(error => {
      console.error('❌ Error in isLoggedIn check:', error);
      // Don't update state on error - keep current state
      return false;
    });
  }

  public isLoggingIn(): boolean {
    return localStorage.getItem("isLoggingIn") === "true";
  }

  public isAuthenticated(): boolean {
    // OIDC-aware authentication check - use current OIDC user state
    const hasValidUser = !!this._user && !this._user.expired && !!this._user.access_token;

    //console.log('🔍 isAuthenticated check:', {
    //   hasUser: !!this._user,
    //   expired: this._user?.expired,
    //   hasAccessToken: !!this._user?.access_token,
    //   result: hasValidUser,
    //   caller: new Error().stack?.split('\n')[1]?.trim()
    // });

    return hasValidUser;
  }

  // Add async version for more comprehensive checks
  public async isAuthenticatedAsync(): Promise<boolean> {
    try {
      // console.log('🔍 isAuthenticatedAsync() called');
      // console.log('Call stack:', new Error().stack?.split('\n').slice(1, 4).join('\n'));

      // Ensure the auth service is initialized before checking authentication
      // console.log('🚀 Calling auth service initialize()...');
      await this.initialize();

      // console.log('✅ Auth service initialization complete, getting user...');
      const user = await this._userManager.getUser();
      const isValid = !!user && !user.expired && !!user.access_token;

      // console.log('🔍 isAuthenticatedAsync check result:', {
      //   hasUser: !!user,
      //   expired: user?.expired,
      //   hasAccessToken: !!user?.access_token,
      //   result: isValid,
      //   userProfile: user?.profile ? { sub: user.profile.sub, name: user.profile.name } : null,
      //   expiresAt: user?.expires_at ? new Date(user.expires_at * 1000).toISOString() : null,
      //   currentTime: new Date().toISOString(),
      //   caller: new Error().stack?.split('\n')[1]?.trim()
      // });

      return isValid;
    } catch (error) {
      console.error('❌ Error in isAuthenticatedAsync:', error);
      return false;
    }
  }

  completeLogin() {
    console.log('🔐 completeLogin() called - processing signin redirect callback...');
    return this._userManager.signinRedirectCallback().then((user) => {
      console.log('✅ Signin redirect callback successful:', {
        hasUser: !!user,
        expired: user?.expired,
        hasAccessToken: !!user?.access_token,
        hasRefreshToken: !!user?.refresh_token,
        hasProfile: !!user?.profile
      });

      // Log token expiration details
      if (user?.expires_at) {
        const expiresInMs = (user.expires_at * 1000) - Date.now();
        const expiresInSeconds = Math.floor(expiresInMs / 1000);
        const expiresInMinutes = Math.floor(expiresInSeconds / 60);
        console.log(`⏰ Initial token lifetime: ${expiresInMinutes}m ${expiresInSeconds % 60}s`);
        console.log(`📅 Token will expire at: ${new Date(user.expires_at * 1000).toLocaleString()}`);

        if (expiresInMinutes < 10) {
          console.warn('⚠️ WARNING: Token lifetime is very short! This may cause frequent re-authentication.');
          console.warn('⚠️ Please check Identity Server configuration for access token lifetime.');
        }
      }

      this._user = user;
      const loginState = !!user && !user.expired;

      console.log('🔔 Emitting loginChanged event with state:', loginState);
      this._loginChangedSubject.next(loginState);

      // Store the access token in localStorage for API calls
      this.updateTasksApiTokenInStorage(user);

      //sb auth - bridge
      this.setIsLoadingSubject(true);
      this.loggingService.logEvent(SECURITY_EVENTS.LOGGED_IN, {
        userId: this._user.profile.sub
      });
      this.loadSecurityContext();
      localStorage.setItem("isLoggingIn", "false");

      // Reset filter checkboxes so a new login never inherits a previous session's state.
      localStorage.removeItem('showCompleted');
      localStorage.removeItem('showRejected');
      this.filterStateService.clearFilterState();

      console.log('✅ completeLogin() finished successfully');
      return user;
    });
  }


  logout() {
    //console.log('🚪 logout() called - starting signout redirect flow');
    this.loggingService.logEvent(SECURITY_EVENTS.LOGGED_OUT);

    //console.log('📊 UserManager instance:', this._userManager);

    // Use the proper clearTokenStorage method instead of localStorage.clear()
    // This preserves OIDC user state needed for signout redirect callback
    this.clearTokenStorage();

    // Clear filter state on logout so new users get default filters
    this.filterStateService.clearFilterState();

    // Clear task filter preferences from localStorage
    localStorage.removeItem('dateRangeKey');
    localStorage.removeItem('myTasks');
    localStorage.removeItem('showCompleted');
    localStorage.removeItem('showRejected');

    //console.log('🌐 Initiating OIDC signout redirect...');
    this._userManager.signoutRedirect();
  }

  completeLogout() {
    this._user = null;
    this.authContext = null;
    this._loginChangedSubject.next(false);
    return this._userManager.signoutRedirectCallback();
  }

  async loadSecurityContext() {
    const accessToken = await this.getAccessToken();

    if (accessToken) {
      // authContext is loaded unconditionally — role and permission enforcement
      // is the backend's responsibility on every API call.
      try {
        this.authContext = new AuthContext();
        this.authContextUpdated.emit(this.authContext);
        this.isProfileLoadedSubject$.next(true);
      } catch (error) {
        this.loggingService.logError(error, SeverityLevel.Error);
        return null;
      }
    } else {
      //console.log('No access token available');
      return null;
    }
  }

  async getAccessToken() {
    // Check if user has been idle too long before returning any token
    if (this.shouldLogoutDueToIdleTimeout()) {
      console.log('🚪 User idle >=15 minutes - redirecting to login instead of returning token');
      this.router.navigate(['/login']);
      this.logout();
      return null;
    }

    try {
      const user = await this._userManager.getUser();

      const timeUntilExpiryMs = user?.expires_at ? (user.expires_at * 1000) - Date.now() : null;
      const minutesRemaining = timeUntilExpiryMs ? Math.floor(timeUntilExpiryMs / 60000) : null;
      const secondsRemaining = timeUntilExpiryMs ? Math.floor((timeUntilExpiryMs % 60000) / 1000) : null;

      // console.log('🔍 [getAccessToken] Token check:', {
      //   hasUser: !!user,
      //   expired: user?.expired,
      //   hasAccessToken: !!user?.access_token,
      //   expiresAt: user?.expires_at ? new Date(user.expires_at * 1000).toLocaleString() : 'N/A',
      //   timeRemaining: timeUntilExpiryMs !== null ? `${minutesRemaining}m ${secondsRemaining}s` : 'N/A'
      // });

      if (!!user && !user.expired && user.access_token) {
        // Check if token is expired or expiring soon (with buffer) - MUST AWAIT!
        const isExpiring = await this.isTokenExpiredOrExpiring(user);
        if (isExpiring) {
          // console.warn('⚠️ [getAccessToken] Token expired or expiring soon (within 30s buffer) - returning null to trigger renewal');
          return null;
        }

        // Update our local user reference if it's different
        if (this._user !== user) {
          // console.log('📝 [getAccessToken] Updating local user reference');
          this._user = user;
        }
        // console.log('✅ [getAccessToken] Returning valid access token');
        return user.access_token;
      } else {
        if (user?.expired) {
          // For expired tokens, redirect to login
          // console.log('🚪 Token expired in getAccessToken - redirecting to login');
          this.router.navigate(['/login']);
          this.logout();
        } else if (!user) {
          // console.log('⚠️ No user found in getAccessToken');
        } else if (!user.access_token) {
          // console.log('⚠️ User has no access token in getAccessToken');
        }
        return null;
      }
    } catch (error) {
      console.error('❌ Error in getAccessToken:', error);
      return null;
    }
  }

  /**
   * Check if token is expired or expiring soon (within buffer time)
   * @param user Optional user object. If not provided, will get current user
   * @returns true if token is expired or will expire within buffer seconds
   */
  async isTokenExpiredOrExpiring(user?: User): Promise<boolean> {
    try {
      const currentUser = user || await this._userManager.getUser();

      if (!currentUser || !currentUser.expires_at) {
        // console.warn('⚠️ [isTokenExpiredOrExpiring] No user or expiry time - considering expired');
        return true; // No user or expiry time = consider expired
      }

      // Check if already marked as expired
      if (currentUser.expired) {
        // console.warn('⚠️ [isTokenExpiredOrExpiring] Token already marked as expired by OIDC client');
        return true;
      }

      // Calculate time until expiry
      const expiryTimeMs = currentUser.expires_at * 1000;
      const currentTimeMs = Date.now();
      const timeUntilExpiryMs = expiryTimeMs - currentTimeMs;
      const bufferMs = this.TOKEN_EXPIRY_BUFFER_SECONDS * 1000;

      const minutesRemaining = Math.floor(timeUntilExpiryMs / 60000);
      const secondsRemaining = Math.floor((timeUntilExpiryMs % 60000) / 1000);

      // Consider expired if within buffer time
      const isExpiring = timeUntilExpiryMs <= bufferMs;

      if (isExpiring) {
        // console.warn(`⏰ [isTokenExpiredOrExpiring] Token expiring in ${minutesRemaining}m ${secondsRemaining}s (within ${this.TOKEN_EXPIRY_BUFFER_SECONDS}s buffer)`);
        // console.warn(`📅 Token expires at: ${new Date(expiryTimeMs).toLocaleString()}`);
      } else {
        // console.log(`✅ [isTokenExpiredOrExpiring] Token still valid for ${minutesRemaining}m ${secondsRemaining}s`);
      }

      return isExpiring;
    } catch (error) {
      console.error('Error checking token expiry:', error);
      return true; // On error, assume expired for safety
    }
  }

  /**
   * Check if we should attempt token renewal based on user activity
   * Renew tokens for any session that has not yet crossed the idle-timeout threshold.
   * @returns true if user is active enough to warrant token renewal
   */
  shouldRenewTokenBasedOnActivity(): boolean {
    const isActive = !this.userActivityService.isUserIdleTooLong();

    if (!isActive) {
      const inactiveTime = this.userActivityService.getTimeSinceLastActivityFormatted();
      // console.log(`💤 User inactive for ${inactiveTime} - skipping token renewal`);
    }

    return isActive;
  }

  /**
   * Check if user has been idle too long (more than 15 minutes) and should be logged out
   * @returns true if user should be logged out due to idle timeout
   */
  shouldLogoutDueToIdleTimeout(): boolean {
    const isIdleTooLong = this.userActivityService.isUserIdleTooLong();

    if (isIdleTooLong) {
      const inactiveTime = this.userActivityService.getTimeSinceLastActivityFormatted();
      // console.log(`⏰ User idle for ${inactiveTime} (>15 minutes) - logout required`);
    }

    return isIdleTooLong;
  }

  // Helper method to ensure tasksApiToken is always stored in localStorage
  private updateTasksApiTokenInStorage(user: User | null) {
    if (user?.access_token) {
      localStorage.setItem('tasksApiToken', 'Bearer ' + user.access_token);
      //console.log('✅ Updated tasksApiToken in localStorage');
    } else {
      localStorage.removeItem('tasksApiToken');
      //console.log('🗑️ Removed tasksApiToken from localStorage (no valid token)');
    }
  }

  // Clear token storage - but preserve OIDC user state in localStorage
private clearTokenStorage() {
  // Only clear session storage, NOT localStorage since we're using localStorage for OIDC user store
  const keysToRemove = [];
  for (let i = 0; i < sessionStorage.length; i++) {
    const key = sessionStorage.key(i);
    if (key && key.startsWith('oidc.')) {
      keysToRemove.push(key);
    }
  }
  keysToRemove.forEach(key => sessionStorage.removeItem(key));

  // Clear the tasksApiToken from localStorage during logout
  localStorage.removeItem('tasksApiToken');

  // Don't clear the main OIDC user from localStorage as we're using it as our userStore
  //console.log('🧹 Cleared sessionStorage OIDC data and tasksApiToken but preserved localStorage user store');
}

  async renewToken(): Promise<User | null> {
    console.log('🔄 renewToken() called');

    // Check if we're in a cooldown period from the last failed attempt
    const timeSinceLastAttempt = Date.now() - this._lastRenewalAttempt;
    if (timeSinceLastAttempt < this._renewalCooldownMs) {
      const remainingCooldown = Math.ceil((this._renewalCooldownMs - timeSinceLastAttempt) / 1000);
      // console.log(`⏰ Token renewal in cooldown for ${remainingCooldown} more seconds`);
      throw new Error(`Token renewal in cooldown. Try again in ${remainingCooldown} seconds.`);
    }

    // Check if user has been idle for more than 15 minutes - if so, logout
    if (this.shouldLogoutDueToIdleTimeout()) {
      // console.log('🚪 User idle for >15 minutes - forcing logout');
      this.router.navigate(['/login']);
      this.logout();
      throw new Error('Session expired due to inactivity - redirecting to login');
    }

    // If a renewal is already in progress, return the existing promise
    if (this._isRenewing && this._renewalPromise !== null) {
      //console.log('🔄 Token renewal already in progress, waiting for existing renewal...');
      return this._renewalPromise;
    }

    // Additional check - if we don't have a current user or refresh token, don't try to renew
    const currentUser = await this._userManager.getUser();
    if (!currentUser?.refresh_token) {
      // console.log('❌ No user or refresh token available for renewal');
      throw new Error('No valid user or refresh token available');
    }

    // Log current token status
    if (currentUser.expires_at) {
      const expiresInMs = (currentUser.expires_at * 1000) - Date.now();
      const expiresInSeconds = Math.floor(expiresInMs / 1000);
      const expiresInMinutes = Math.floor(expiresInSeconds / 60);
      // console.log(`⏰ Current token expires in: ${expiresInMinutes}m ${expiresInSeconds % 60}s`);
    }

    // Access tokens can be renewed after expiry as long as the refresh token is still valid.
    if (currentUser.expired || (currentUser.expires_at && currentUser.expires_at * 1000 <= Date.now())) {
      console.log('⏰ Access token already expired - attempting refresh-token renewal');
    }

    // console.log('🚀 Starting new token renewal process...');

    // Update last attempt time
    this._lastRenewalAttempt = Date.now();

    // Set the renewal flag and create a new promise
    this._isRenewing = true;
    this._renewalPromise = this.performTokenRenewal();

    try {
      const result = await this._renewalPromise;
      //console.log('✅ Token renewal completed successfully');
      // Reset cooldown on success
      this._lastRenewalAttempt = 0;
      return result;
    } catch (error) {
      console.error('❌ Token renewal failed:', error);
      // Keep the cooldown time on failure
      throw error;
    } finally {
      // Always reset the renewal state
      //console.log('🏁 Resetting renewal state');
      this._isRenewing = false;
      this._renewalPromise = null;
    }
  }

  private async performTokenRenewal(): Promise<User | null> {
    try {
      // console.log('=== Starting Token Renewal Debug ===');

      // Get the current user state
      const currentUser = await this._userManager.getUser();
        // console.log('Current user before renewal:', {
        //   expired: currentUser?.expired,
        //   expires_at: currentUser?.expires_at,
        //   expiresAtDate: currentUser?.expires_at ? new Date(currentUser.expires_at * 1000).toLocaleString() : 'N/A',
        //   has_access_token: !!currentUser?.access_token,
        //   has_refresh_token: !!currentUser?.refresh_token,
        //   access_token_first_50: currentUser?.access_token?.substring(0, 50) || 'None',
        //   refresh_token_first_20: currentUser?.refresh_token?.substring(0, 20) || 'None'
        // });

      // Validate we have what we need for renewal
      if (!currentUser?.refresh_token) {
        // console.log('❌ No valid user or refresh token found');
        throw new Error('No valid user or refresh token available');
      }

      // Check if we should force full authentication based on time
      if (this.shouldForceFullAuth()) {
        //console.log('⏰ Time limit reached - will show re-authentication warning but not force logout');

        // Show warning to user but don't force logout immediately
        this.showReauthenticationWarning();

        // Try renewal anyway - if it fails, then we'll handle it gracefully
        //console.log('🔄 Attempting token renewal despite time limit...');
      }

      // Check if token is actually close to expiry before attempting renewal
      if (currentUser.expires_at) {
        const expiryTime = currentUser.expires_at * 1000;
        const timeUntilExpiry = expiryTime - Date.now();
        const fiveMinutesMs = 5 * 60 * 1000;

        if (timeUntilExpiry > fiveMinutesMs) {
          //console.log(`Token not close to expiry (${Math.floor(timeUntilExpiry / 1000 / 60)} minutes remaining), returning current user`);
          return currentUser;
        }
      }

      // Attempt silent renewal
      // console.log(`🔄 Making silent renewal request at timestamp: ${Date.now()}`);

      // Use signinSilent which should handle refresh token rotation properly
      const silentResult = await this._userManager.signinSilent();

      if (!silentResult) {
        throw new Error('Silent renewal returned null result');
      }

        // console.log('✅ Silent renewal result:', {
        //   expired: silentResult.expired,
        //   expires_at: silentResult.expires_at,
        //   expiresAtDate: silentResult.expires_at ? new Date(silentResult.expires_at * 1000).toLocaleString() : 'N/A',
        //   has_access_token: !!silentResult.access_token,
        //   has_refresh_token: !!silentResult.refresh_token,
        //   access_token_first_50: silentResult.access_token?.substring(0, 50) || 'None',
        //   refresh_token_first_20: silentResult.refresh_token?.substring(0, 20) || 'None'
        // });

      // Log new token lifetime
      if (silentResult.expires_at) {
        const expiresInMs = (silentResult.expires_at * 1000) - Date.now();
        const expiresInSeconds = Math.floor(expiresInMs / 1000);
        const expiresInMinutes = Math.floor(expiresInSeconds / 60);
        // console.log(`⏰ New token lifetime: ${expiresInMinutes}m ${expiresInSeconds % 60}s`);

        if (expiresInMinutes < 10) {
          console.warn('⚠️ WARNING: Renewed token lifetime is very short!');
          console.warn('⚠️ Identity Server may be configured with short access token lifetime.');
          console.warn('⚠️ Consider increasing AccessTokenLifetime in Identity Server configuration.');
        }
      }

      // Verify we got a new token
      if (currentUser.access_token && silentResult.access_token) {
        const tokensIdentical = currentUser.access_token === silentResult.access_token;
        //console.log('Tokens are identical?', tokensIdentical);

        if (tokensIdentical) {
          console.warn('🚨 Received identical token - possible caching issue');
          // Don't fail immediately, but log for debugging
        }
      }

      // Update our local user reference and emit state changes
      this._user = silentResult;

      // Explicitly update the authentication state to ensure all components are notified
      this._loginChangedSubject.next(true);
      this.setIsLoadingSubject(true);

      // Force the user manager to store the new user in localStorage
      // This ensures persistence across page reloads and prevents logout
      try {
        await this._userManager.storeUser(silentResult);
        //console.log('✅ New user stored in localStorage successfully');

        // Also update the tasksApiToken in localStorage for API calls
        this.updateTasksApiTokenInStorage(silentResult);

        // Verify the user was actually stored
        const storedUser = await this._userManager.getUser();
        //console.log('🔍 Verification - user retrieved after storage:', {
        //   hasUser: !!storedUser,
        //   expired: storedUser?.expired,
        //   hasAccessToken: !!storedUser?.access_token,
        //   hasRefreshToken: !!storedUser?.refresh_token,
        //   tokensMatch: storedUser?.access_token === silentResult.access_token
        // });
      } catch (storeError) {
        console.warn('⚠️ Failed to store user in localStorage:', storeError);
      }

      // Refresh user state to ensure all components see the updated state
      await this.refreshUserState();

      // Log successful renewal
      this.loggingService.logEvent(SECURITY_EVENTS.TOKEN_RENEWED, {
        userId: this._user?.profile?.sub,
        newExpiration: silentResult.expires_at
      });

      //console.log('✅ Token renewal completed successfully - user state updated');
      //console.log('=== End Token Renewal Debug ===');

      return silentResult;

    } catch (error) {
      console.error('❌ Silent sign-in failed:', error);

      const errorMessage = error instanceof Error ? error.message : 'Unknown error';

      // Handle specific error types - but DON'T clear user state immediately
      // This prevents the user from being logged out when refresh token rotation occurs
      if (errorMessage.includes('invalid_grant')) {
        //console.log('🚨 Refresh token invalid (likely already used or expired)');
        //console.log('⚠️ Showing re-authentication warning but keeping user logged in');

        // Show warning to user but don't clear their session
        this.showReauthenticationWarning();

        throw new Error('Refresh token invalid - re-authentication required');
      }

      if (errorMessage.includes('login_required') || errorMessage.includes('interaction_required')) {
        //console.log('🔐 Server requires user interaction - full login needed');
        //console.log('⚠️ Showing re-authentication warning but keeping user logged in');

        // Show warning to user but don't clear their session
        this.showReauthenticationWarning();

        throw new Error('User interaction required - full login needed');
      }

      // For network errors or temporary issues, don't clear state either
      if (errorMessage.includes('Network') || errorMessage.includes('timeout') || errorMessage.includes('fetch')) {
        //console.log('🌐 Network or temporary error during renewal');
        //console.log('⚠️ Showing re-authentication warning but keeping user logged in');

        // Show warning but don't clear state for network issues
        this.showReauthenticationWarning();

        throw error;
      }

      // Only clear state for truly unrecoverable errors
      //console.log('🔄 Unrecoverable error during silent renewal');
      //console.log('⚠️ Showing re-authentication warning but keeping user logged in');

      // Even for other errors, show warning instead of clearing state
      this.showReauthenticationWarning();

      throw error;
    }
  }

  // Helper method to clear all user state consistently - ONLY for explicit logout
  private async clearUserState(): Promise<void> {
    // Don't clear user state if we're currently renewing
    if (this._isRenewing) {
      //console.log('⏳ Not clearing user state - renewal in progress');
      return;
    }

    try {
      //console.log('🧹 Clearing user state for explicit logout...');

      // Clear from user manager - this should only be called during explicit logout
      await this._userManager.removeUser();

      // Clear session storage but preserve localStorage OIDC user store
      this.clearTokenStorage();

      // Reset our local state
      this._user = null;
      this.authContext = null;
      this.setLoginState(false, 'clearUserState method');
      this.setIsLoadingSubject(false);

      //console.log('🧹 User state cleared successfully');
    } catch (error) {
      console.error('Error clearing user state:', error);
    }
  }

  // Check if we should force a full authentication instead of silent renewal
  private shouldForceFullAuth(): boolean {
    // Force full auth every 24 hours instead of 8 hours to reduce interruptions
    // This gives users a full day of work before requiring re-authentication
    const TWENTY_FOUR_HOURS_MS = 24 * 60 * 60 * 1000;
    const timeSinceLastFullAuth = Date.now() - this.lastFullAuthTime;
    const hoursElapsed = Math.floor(timeSinceLastFullAuth / 1000 / 60 / 60);

    //console.log(`Time since last full auth: ${hoursElapsed} hours`);

    // Only force full auth if it's been more than 24 hours
    const shouldForce = timeSinceLastFullAuth > TWENTY_FOUR_HOURS_MS;

    if (shouldForce) {
      //console.log('⏰ 24-hour limit reached - will require full authentication on next login attempt');
    }

    return shouldForce;
  }

  // Helper method to track login state changes with debugging
  private setLoginState(isLoggedIn: boolean, context: string = 'Unknown') {
    //console.log(`🔄 setLoginState() called with:`, {
    //   newState: isLoggedIn,
    //   context: context,
    //   hasUser: !!this._user,
    //   userExpired: this._user?.expired,
    //   timestamp: new Date().toISOString()
    // });

    if (!isLoggedIn) {
      console.group('🚨 USER LOGOUT EVENT');
      //console.log('Logout triggered by:', context);
      //console.log('Current user state:', {
      //   hasUser: !!this._user,
      //   expired: this._user?.expired,
      //   hasAccessToken: !!this._user?.access_token,
      //   isRenewing: this._isRenewing
      // });
      //console.log('Call stack:');
      console.trace();
      console.groupEnd();
    } else {
      //console.log('✅ User login state set to true by:', context);
    }

    this._loginChangedSubject.next(isLoggedIn);
  }

  setIsLoadingSubject(loaded: boolean) {
    const previousAuth = this.isAuthenticatedSubject$.value;

    //console.log('🔄 Setting auth state:', {
    //   loaded: loaded,
    //   previousAuth: previousAuth,
    //   stackTrace: new Error().stack?.split('\n').slice(1, 4).join('\n')
    // });

    this.isDoneLoadingSubject$.next(loaded);
    this.isAuthenticatedSubject$.next(loaded);

    if (!loaded && previousAuth) {
      //console.log('🚨 User being marked as NOT authenticated! Previous state was authenticated.');
      //console.log('🔍 Call stack for logout:', new Error().stack);
    }
  }

  // Helper method to show re-authentication warning
  private showReauthenticationWarning(): void {
    console.warn('🔐 Authentication session has expired. You may need to sign in again to continue.');

    // You can customize this to show a toast notification, modal, or other UI element
    // For now, we'll just log it and emit an event
    this.loggingService.logEvent(SECURITY_EVENTS.SESSION_EXPIRED, {
      message: 'User session expired - re-authentication may be required'
    });

    // Emit an event that components can listen to for showing UI notifications
    this.authContextUpdated.emit(null);
  }

  // Method to perform login without immediate redirect (for graceful handling)
  public requestAuthentication(): void {
    //console.log('Authentication requested by user or application');
    this.login();
  }

  // Method to check if user needs to re-authenticate
  public needsReauthentication(): boolean {
    return !this._user || this._user.expired;
  }

  // Debug method to get current authentication state
  public getAuthenticationState(): any {
    return {
      hasUser: !!this._user,
      userExpired: this._user?.expired,
      hasAccessToken: !!this._user?.access_token,
      hasRefreshToken: !!this._user?.refresh_token,
      expiresAt: this._user?.expires_at,
      timeUntilExpiry: this._user?.expires_at ? (this._user.expires_at * 1000) - Date.now() : null,
      lastFullAuthTime: this.lastFullAuthTime,
      timeSinceLastFullAuth: Date.now() - this.lastFullAuthTime,
      isRenewing: this._isRenewing,
      lastRenewalAttempt: this._lastRenewalAttempt
    };
  }

  // Method to log current authentication state for debugging
  public logAuthState(context: string = 'Unknown'): void {
    const state = this.getAuthenticationState();
    //console.log(`🔍 Auth State (${context}):`, state);
  }

  // Enhanced method to check token validity without triggering renewals
  public async isTokenValid(): Promise<boolean> {
    try {
      const user = await this._userManager.getUser();
      const isValid = !!user && !user.expired && !!user.access_token;

      if (!isValid) {
        //console.log('Token validation failed:', {
        //   hasUser: !!user,
        //   expired: user?.expired,
        //   hasToken: !!user?.access_token,
        //   expiresAt: user?.expires_at
        // });
      }

      return isValid;
    } catch (error) {
      console.error('Error checking token validity:', error);
      return false;
    }
  }

  // Method to refresh user state after token renewal
  private async refreshUserState(): Promise<void> {
    try {
      const user = await this._userManager.getUser();
      //console.log('🔄 Refreshing user state:', {
      //   hasUser: !!user,
      //   expired: user?.expired,
      //   hasAccessToken: !!user?.access_token
      // });

      if (user && !user.expired && user.access_token) {
        this._user = user;
        this._loginChangedSubject.next(true);
        this.setIsLoadingSubject(true);
        //console.log('✅ User state refreshed successfully');
      } else {
        console.warn('⚠️ User state refresh failed - user expired or missing');
      }
    } catch (error) {
      console.error('❌ Error refreshing user state:', error);
    }
  }

  /**
   * Initialize the auth service and load user from storage
   * This should be called once during app startup to ensure proper user state restoration
   */
  async initialize(): Promise<void> {
    //console.log('🔄 Auth service initialize() called');
    //console.log('Current state:', {
    //   isInitialized: this._isInitialized,
    //   hasInitPromise: !!this._initializationPromise,
    //   stackTrace: new Error().stack?.split('\n').slice(1, 3).join('\n')
    // });

    if (this._isInitialized) {
      //console.log('✅ Auth service already initialized, returning resolved promise');
      return this._initializationPromise || Promise.resolve();
    }

    if (this._initializationPromise) {
      //console.log('⏳ Auth service initialization already in progress, waiting...');
      return this._initializationPromise;
    }

    //console.log('🚀 Starting new auth service initialization...');
    this._initializationPromise = this.performInitialization();
    return this._initializationPromise;
  }

  private async performInitialization(): Promise<void> {
    try {
      //console.log('🚀 performInitialization() starting...');
      //console.log('📊 Current auth service state before initialization:', {
      //   hasUser: !!this._user,
      //   userExpired: this._user?.expired,
      //   hasUserManager: !!this._userManager,
      //   timestamp: new Date().toISOString()
      // });

      // Get user from storage - this will load from localStorage if configured
      //console.log('🔍 Calling _userManager.getUser() to load from storage...');
      const user = await this._userManager.getUser();

      //console.log('👤 Initial user load from storage result:', {
      //   hasUser: !!user,
      //   expired: user?.expired,
      //   hasAccessToken: !!user?.access_token,
      //   hasRefreshToken: !!user?.refresh_token,
      //   expiresAt: user?.expires_at,
      //   timeUntilExpiry: user?.expires_at ? (user.expires_at * 1000) - Date.now() : null,
      //   profile: user?.profile ? { sub: user.profile.sub, name: user.profile.name } : null
      // });

      if (user && !user.expired && user.access_token) {
        //console.log('✅ Found valid, non-expired user with access token');
        this._user = user;

        //console.log('🔄 Setting login state to true during initialization...');
        this.setLoginState(true, 'Auth service initialization - valid user');
        this.setIsLoadingSubject(true);

        // Load security context for authenticated user
        //console.log('🔐 Loading security context for authenticated user...');
        await this.loadSecurityContext();
        //console.log('✅ Auth service initialized with authenticated user');
      } else if (user && user.expired && user.refresh_token) {
        //console.log('🔄 User token expired on startup, attempting renewal...');
        //console.log('Token expiry details:', {
        //   expired: user.expired,
        //   expiresAt: user.expires_at,
        //   timeUntilExpiry: user.expires_at ? (user.expires_at * 1000) - Date.now() : null,
        //   hasRefreshToken: !!user.refresh_token
        // });

        this._user = user;

        try {
          //console.log('🔄 Starting token renewal during initialization...');
          const renewedUser = await this.renewToken();
          if (renewedUser) {
            //console.log('✅ Token renewal successful during initialization');
          } else {
            console.warn('⚠️ Token renewal failed during initialization - no user returned');
            this.setLoginState(false, 'Auth service initialization - renewal failed');
          }
        } catch (error) {
          console.error('❌ Token renewal error during initialization:', error);
          this.setLoginState(false, 'Auth service initialization - renewal error');
        }
      } else {
        //console.log('❌ No valid user found during initialization');
        //console.log('User state details:', {
        //   hasUser: !!user,
        //   expired: user?.expired,
        //   hasAccessToken: !!user?.access_token,
        //   hasRefreshToken: !!user?.refresh_token
        // });

        this._user = null;
        //console.log('🔄 Setting login state to false - no valid user');
        this.setLoginState(false, 'Auth service initialization - no user');
        this.setIsLoadingSubject(false);
      }

      this._isInitialized = true;
      //console.log('✅ Auth service initialization completed successfully');
    } catch (error) {
      console.error('❌ Error during auth service initialization:', error);
      console.error('Error details:', {
        message: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined
      });

      this._isInitialized = true;
      //console.log('🔄 Setting login state to false due to initialization error');
      this.setLoginState(false, 'Auth service initialization - error');
      throw error;
    }
  }

  // ==================== TESTING UTILITIES ====================
  // These methods are for testing token expiration and session management

  /**
   * Testing utility: Force token to appear expired
   */
  public async forceTokenExpiration(): Promise<void> {
    //console.log('🧪 TESTING: Forcing token expiration...');

    try {
      const user = await this._userManager.getUser();
      if (user && user.access_token) {
        // Extract user data safely
        let userData: any;

        try {
          // Try to get the storage string if the method exists
          if (typeof user.toStorageString === 'function') {
            userData = JSON.parse(user.toStorageString());
          } else {
            // Fallback to copying properties directly
            userData = {
              access_token: user.access_token,
              refresh_token: user.refresh_token,
              token_type: user.token_type,
              scope: user.scope,
              expires_at: user.expires_at,
              expired: user.expired,
              id_token: (user as any).id_token,
              session_state: (user as any).session_state,
              state: (user as any).state
            };
          }
        } catch (storageError) {
          console.warn('🧪 Could not get user storage string, using direct properties:', storageError);
          userData = {
            access_token: user.access_token,
            refresh_token: user.refresh_token,
            token_type: user.token_type,
            scope: user.scope,
            expires_at: user.expires_at,
            expired: user.expired
          };
        }

        // Force token to be expired
        userData.expired = true;
        userData.expires_at = Math.floor(Date.now() / 1000) - 3600; // Set to 1 hour ago

        // Create new user and store it
        const expiredUser = new User(userData);
        await this._userManager.storeUser(expiredUser);
        this._user = expiredUser;

        //console.log('🧪 Token artificially expired for testing');
        //console.log('🧪 Expired token details:', {
        //   expired: expiredUser.expired,
        //   expires_at: expiredUser.expires_at,
        //   current_time: Math.floor(Date.now() / 1000)
        // });

        // Trigger token check to see if system detects the expiry
        //console.log('🧪 Triggering token renewal check...');
        try {
          await this.renewToken();
          //console.log('🧪 Renewal attempt completed after forcing expiry');
        } catch (renewError) {
          //console.log('🧪 Renewal failed as expected with expired token:', renewError);
        }

        // Force a check of the token status
        this._loginChangedSubject.next(false);

        //console.log('🧪 Token expiry forced successfully - check token status in testing panel');
      } else {
        //console.log('🧪 ERROR: No valid user or access token found');
      }
    } catch (error) {
      console.error('🧪 ERROR in forceTokenExpiration:', error);
      throw error;
    }
  }

  /**
   * Testing utility: Set token to expire in specified minutes
   */
  public async simulateTokenNearExpiry(minutesFromNow: number = 2): Promise<void> {
    //console.log(`🧪 TESTING: Setting token to expire in ${minutesFromNow} minutes...`);

    try {
      const user = await this._userManager.getUser();
      if (user && user.access_token) {
        const expiryTime = Math.floor((Date.now() + (minutesFromNow * 60 * 1000)) / 1000);

        // Extract user data safely
        let userData: any;

        try {
          // Try to get the storage string if the method exists
          if (typeof user.toStorageString === 'function') {
            userData = JSON.parse(user.toStorageString());
          } else {
            // Fallback to copying properties directly
            userData = {
              access_token: user.access_token,
              refresh_token: user.refresh_token,
              token_type: user.token_type,
              scope: user.scope,
              expires_at: user.expires_at,
              expired: user.expired,
              id_token: (user as any).id_token,
              session_state: (user as any).session_state,
              state: (user as any).state
            };
          }
        } catch (storageError) {
          console.warn('🧪 Could not get user storage string, using direct properties:', storageError);
          userData = {
            access_token: user.access_token,
            refresh_token: user.refresh_token,
            token_type: user.token_type,
            scope: user.scope,
            expires_at: user.expires_at,
            expired: user.expired
          };
        }

        // Update expiry time
        userData.expires_at = expiryTime;
        userData.expired = false;

        // Create new user and store it
        const nearExpiryUser = new User(userData);
        await this._userManager.storeUser(nearExpiryUser);
        this._user = nearExpiryUser;

        //console.log(`🧪 Token will expire at: ${new Date(expiryTime * 1000).toISOString()}`);
        //console.log('🧪 Token expiry simulation completed successfully');
      } else {
        //console.log('🧪 ERROR: No valid user or access token found');
      }
    } catch (error) {
      console.error('🧪 ERROR in simulateTokenNearExpiry:', error);
      throw error;
    }
  }

  /**
   * Testing utility: Get detailed authentication state for debugging
   */
  public getTestingInfo(): any {
    return {
      currentUser: this._user ? {
        expired: this._user.expired,
        expires_at: this._user.expires_at,
        expiryDate: this._user.expires_at ? new Date(this._user.expires_at * 1000).toISOString() : null,
        timeUntilExpiry: this._user.expires_at ? (this._user.expires_at * 1000) - Date.now() : null,
        hasAccessToken: !!this._user.access_token,
        hasRefreshToken: !!this._user.refresh_token
      } : null,
      isRenewing: this._isRenewing,
      lastRenewalAttempt: this._lastRenewalAttempt,
      renewalCooldown: this._renewalCooldownMs,
      isInitialized: this._isInitialized
    };
  }

  /**
   * Testing utility: Force a token renewal attempt
   */
  public async forceTokenRenewal(): Promise<void> {
    //console.log('🧪 TESTING: Forcing token renewal attempt...');
    try {
      const result = await this.renewToken();
      //console.log('🧪 Forced renewal result:', !!result);
    } catch (error) {
      //console.log('🧪 Forced renewal error:', error);
    }
  }

  /**
   * Testing utility: Reset renewal cooldown for immediate testing
   */
  public resetRenewalCooldown(): void {
    //console.log('🧪 TESTING: Resetting renewal cooldown...');
    this._lastRenewalAttempt = 0;
  }
}
